import React, { useEffect, useMemo, useState } from 'react';
import { RunStatePill, runState } from '../EtlPages';
import { MobileCard, MobileGroup, MobileMore, clockTime, arrivalLabel } from './EtlMobileKit';

const ATTENTION = new Set(['failed', 'missed', 'needs_review']);
const PAGE = 40;

// Shorter pill labels — the desktop ones ("Expected — not received") don't
// fit beside a file name on a 390px card.
const SHORT_LABEL = {
  missed: 'Not received',
  pending: 'Waiting',
  needs_review: 'Review',
};

/** Same mapping FilesTable uses — the backend's status, `received` folded into pending. */
const displayStatus = (record) => ({
  needs_review: 'needs_review',
  processed: 'processed',
  received: 'pending',
  pending: 'pending',
  missed: 'missed',
  failed: 'failed',
}[record.status] || 'pending');

function subLine(record) {
  const property = record.metadata?.locationName || record.locationName || `Hotel ${record.hotelId}`;
  const receivedAt = record.reception?.receivedAt || record.tracking?.lastReceived;
  const time = clockTime(receivedAt);
  if (!time) return `${property} · not received`;
  const via = arrivalLabel(record.reception?.receivedFrom || 'email');
  return `${property} · ${time} via ${via}`;
}

/**
 * Phone form of FilesTable: cards grouped "Needs attention" (failed, not
 * received, needs review) first, then everything else, keeping the order the
 * tab already sorted them in. Tapping a card selects it, exactly as a row
 * click does on desktop.
 */
export default function FilesCardList({ records, selectedRecord, onSelectRecord }) {
  const [shown, setShown] = useState(PAGE);
  useEffect(() => setShown(PAGE), [records]);

  const { attention, rest } = useMemo(() => {
    const a = [];
    const r = [];
    for (const record of records) (ATTENTION.has(displayStatus(record)) ? a : r).push(record);
    return { attention: a, rest: r };
  }, [records]);

  if (!records.length) {
    return (
      <div className="ep-m ep-m-list">
        <div className="ep-m-empty">No files match these filters.</div>
      </div>
    );
  }

  const isSelected = (record) => (
    selectedRecord?.id === record.id
    || (selectedRecord?.hotelId === record.hotelId && selectedRecord?.fileType === record.fileType)
  );

  // One budget across both groups so "Show more" grows the list in order.
  const attentionShown = attention.slice(0, shown);
  const restShown = rest.slice(0, Math.max(0, shown - attention.length));

  const card = (record, index) => {
    const status = displayStatus(record);
    const s = runState(status);
    const name = record.displayName || record.fileType || 'File';
    return (
      <MobileCard
        key={record.id || `${record.hotelId}-${record.fileType}-${index}`}
        icon={s.Icon}
        tone={s.tone}
        title={name}
        sub={subLine(record)}
        trailing={<RunStatePill status={status} label={SHORT_LABEL[status]} title={record.dlq?.lastErrorMessage || undefined} />}
        selected={isSelected(record)}
        onClick={() => onSelectRecord(record)}
        ariaLabel={`${name}, ${record.metadata?.locationName || record.locationName || ''}, ${s.label}`}
      />
    );
  };

  return (
    <div className="ep-m" data-testid="files-card-list">
      {attentionShown.length ? (
        <MobileGroup label="Needs attention" count={attention.length} warn>
          {attentionShown.map(card)}
        </MobileGroup>
      ) : null}
      {restShown.length ? (
        <MobileGroup label={attention.length ? 'Everything else' : 'Today'} count={rest.length}>
          {restShown.map(card)}
        </MobileGroup>
      ) : null}
      {shown < records.length ? (
        <div className="ep-m-list" style={{ marginTop: 10 }}>
          <MobileMore shown={shown} total={records.length} step={PAGE} onMore={setShown} />
        </div>
      ) : null}
    </div>
  );
}
